import type { Session, User } from '@supabase/supabase-js';
import { useState, useEffect } from 'react';

import { collapseSidebarForPostLogin } from '../modules/shell/sidebarState';
import { supabase } from '../lib/supabase';
import { appLogger } from '../utils/appLogger';
import { trackLogout } from '../utils/metaPixel';

// Module-level timestamp: shared across all hook instances.
// Used to detect the OAuth race condition where Supabase fires SIGNED_OUT
// for the invalidated old token ~1-3s after a fresh SIGNED_IN (Google GIS).
let lastSignInTimestamp = 0;

// Captured session from the most recent SIGNED_IN event.
// When SIGNED_OUT fires during the grace period, Supabase has already cleared
// localStorage, so getSession() returns null. We restore from this snapshot.
let lastSignInSession: import('@supabase/supabase-js').Session | null = null;

// Module-level grace-period flag.
// Set to true while the SIGNED_OUT handler is polling for a live session.
// AppStateManager checks this to avoid a premature security redirect during recovery.
export let isSignedOutGracePeriodActive = false;

export interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  error: string | null;
}

export const useAuthentication = () => {
  const [authState, setAuthState] = useState<AuthState>({
    session: null,
    user: null,
    loading: true,
    error: null
  });

  // Initialize authentication state
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Prerender build: never call Supabase auth in headless Chrome (can hang >120s).
        if (typeof window !== 'undefined'
          && (window as { __GGJ_PRERENDER__?: boolean }).__GGJ_PRERENDER__) {
          if (isMounted) {
            setAuthState({
              session: null,
              user: null,
              loading: false,
              error: null,
            });
          }
          return;
        }

        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          appLogger.error('Error getting session', error);
          if (isMounted) {
            setAuthState({
              session: null,
              user: null,
              loading: false,
              error: error.message
            });
          }
          return;
        }

        if (isMounted) {
          setAuthState({
            session,
            user: session?.user || null,
            loading: false,
            error: null
          });
        }

      } catch (error) {
        appLogger.error('Error initializing auth', error);
        if (isMounted) {
          setAuthState({
            session: null,
            user: null,
            loading: false,
            error: error instanceof Error ? error.message : 'Authentication error'
          });
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        appLogger.debug('Auth state changed:', event, 'Session exists:', !!session);
        
        // CRITICAL: Check if we're in password reset flow before any redirects
        const urlParams = new URLSearchParams(window.location.search);
        const type = urlParams.get('type');
        const tokenHash = urlParams.get('token_hash');
        const currentPath = window.location.pathname;
        const mandatoryReset = sessionStorage.getItem('mandatory_password_reset');
        const resetInProgress = sessionStorage.getItem('password_reset_in_progress');
        
        // If we're in password reset flow, don't trigger any redirects
        const isPasswordResetFlow = (
          type === 'recovery' || 
          tokenHash || 
          mandatoryReset === 'true' || 
          resetInProgress === 'true' ||
          currentPath === '/auth/confirm' ||
          currentPath === '/reset-password'
        );
        
        if (isPasswordResetFlow) {
          appLogger.debug('SECURITY: Password reset flow detected - blocking auth redirects');
          setAuthState({
            session,
            user: session?.user || null,
            loading: false,
            error: null
          });
          return; // Exit early, no redirects
        }

        // During payment return, SIGNED_OUT may be due to 429 – don't flip to null until recovery attempt fails
        const paymentInProgress = localStorage.getItem('payment_in_progress');
        // During OAuth token rotation, SIGNED_OUT fires for the old token 1-3 s after
        // SIGNED_IN. Don't null out auth state until the grace-period check confirms there
        // really is no session — otherwise the user briefly sees the logged-out UI.
        const isOAuthGracePeriod = event === 'SIGNED_OUT' && (Date.now() - lastSignInTimestamp) < 5000;
        if ((event === 'SIGNED_OUT' && paymentInProgress) || isOAuthGracePeriod) {
          // Will attempt recovery in SIGNED_OUT block; keep current state until then
        } else {
          setAuthState({
            session,
            user: session?.user || null,
            loading: false,
            error: null
          });
        }
        
        // Handle post-authentication navigation
        if (event === 'SIGNED_IN' && session) {
          // Mark the exact moment of sign-in for the OAuth race-condition guard below.
          // Also capture the full session object so we can restore it via setSession()
          // if SIGNED_OUT clears localStorage before the grace period finishes polling.
          lastSignInTimestamp = Date.now();
          lastSignInSession = session;
          // Clear any sign-out flag on successful sign-in
          localStorage.removeItem('user_signed_out');
          // Clear stale payment flag on fresh sign-in — but never during an
          // active Cashfree return (would re-enable home/agent hijack).
          const paymentReturnActive =
            currentPath === '/payment-success'
            || currentPath === '/checkout'
            || localStorage.getItem('payment_in_progress') === 'true';
          if (!paymentReturnActive) {
            localStorage.removeItem('payment_in_progress');
          }
          appLogger.debug('SIGNED_IN: Cleared any signed_out flag');
          
          // Collapse sidebar on auth pages; AppStateManager owns post-login routing
          // (avoids racing pushState/replaceState with handleAuthSuccess + init effect).
          if (currentPath === '/login' || currentPath === '/signup' || currentPath === '/') {
            collapseSidebarForPostLogin();
          }
        }
        
        // Handle sign out
        if (event === 'SIGNED_OUT') {
          // --- OAuth race-condition guard ---
          // After Google (or any OAuth) sign-in, Supabase fires SIGNED_OUT for the
          // invalidated old token 1-3 seconds after SIGNED_IN. If we clear state here
          // the user gets kicked out immediately. Detect this by checking whether
          // SIGNED_IN fired within the last 5 seconds; if so, verify the live session
          // before doing anything.
          const timeSinceSignIn = Date.now() - lastSignInTimestamp;
          if (timeSinceSignIn < 5000) {
            appLogger.debug('SIGNED_OUT within 5s of SIGNED_IN – verifying live session before clearing');
            isSignedOutGracePeriodActive = true;
            // Poll up to 8 × 600 ms = 4.8 s. Google GIS new session can take 2-3 s to
            // be written to storage, so a single check at 1.5 s misses it consistently.
            (async () => {
              try {
                const MAX_ATTEMPTS = 8;
                const POLL_INTERVAL = 600;
                for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                  await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
                  try {
                    const { data: { session: current } } = await supabase.auth.getSession();
                    if (current) {
                      appLogger.debug(`Live session confirmed on attempt ${attempt} – ignoring stale SIGNED_OUT from OAuth token rotation`);
                      setAuthState({ session: current, user: current.user, loading: false, error: null });
                      return;
                    }
                    appLogger.debug(`Attempt ${attempt}/${MAX_ATTEMPTS}: no live session yet`);
                  } catch (_) { /* network blip – keep polling */ }
                }
                // Fallback 1: Try refreshSession (uses refresh token if still in memory)
                try {
                  const { data: { session: refreshed } } = await supabase.auth.refreshSession();
                  if (refreshed) {
                    appLogger.debug('Session recovered via refreshSession after polling – ignoring stale SIGNED_OUT');
                    setAuthState({ session: refreshed, user: refreshed.user, loading: false, error: null });
                    return;
                  }
                } catch (_) { /* ignore */ }
                // Fallback 2: Restore from the captured SIGNED_IN tokens.
                // Supabase clears localStorage when SIGNED_OUT fires, so getSession()
                // returns null even though we just signed in. Re-exchange the tokens we
                // captured at SIGNED_IN time to re-establish the session.
                if (lastSignInSession) {
                  try {
                    const { data: { session: restored } } = await supabase.auth.setSession({
                      access_token: lastSignInSession.access_token,
                      refresh_token: lastSignInSession.refresh_token,
                    });
                    if (restored) {
                      appLogger.debug('Session restored via setSession from captured SIGNED_IN tokens – ignoring stale SIGNED_OUT');
                      lastSignInSession = null;
                      setAuthState({ session: restored, user: restored.user, loading: false, error: null });
                      return;
                    }
                  } catch (_) { /* ignore */ }
                }
                appLogger.debug('No live session after grace period – clearing state');
                sessionStorage.clear();
                localStorage.removeItem('feedback_dont_ask_again');
                localStorage.removeItem('last_feedback_date');
                setAuthState({ session: null, user: null, loading: false, error: null });
              } finally {
                isSignedOutGracePeriodActive = false;
              }
            })();
            return;
          }

          // --- Payment recovery guard ---
          const paymentInProgress = localStorage.getItem('payment_in_progress');
          if (paymentInProgress) {
            appLogger.debug('SIGNED_OUT during payment - attempting recovery');
            setTimeout(async () => {
              try {
                const { data: { session: recovered }, error } = await supabase.auth.refreshSession();
                if (!error && recovered) {
                  appLogger.debug('Session recovered after SIGNED_OUT');
                  setAuthState({ session: recovered, user: recovered.user, loading: false, error: null });
                  return;
                }
              } catch (_) { /* ignore */ }
              appLogger.debug('🚪 Recovery failed – clearing session data');
              sessionStorage.clear();
              localStorage.removeItem('feedback_dont_ask_again');
              localStorage.removeItem('last_feedback_date');
              setAuthState({ session: null, user: null, loading: false, error: null });
            }, 2000);
            return;
          }

          appLogger.debug('SIGNED_OUT: Clearing session data');
          sessionStorage.clear();
          localStorage.removeItem('feedback_dont_ask_again');
          localStorage.removeItem('last_feedback_date');
          appLogger.debug('SIGNED_OUT: Session data cleared');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sign out function
  const signOut = async () => {
    try {
      appLogger.debug('SIGN OUT: Starting');
      trackLogout();
      
      // CRITICAL: Set signed_out flag FIRST to prevent back button bypass
      // This persists across page navigations and prevents re-entry
      localStorage.setItem('user_signed_out', Date.now().toString());
      
      // Clear session markers
      sessionStorage.clear();
      localStorage.removeItem('feedback_dont_ask_again');
      localStorage.removeItem('last_feedback_date');
      
      appLogger.debug('SIGN OUT: Flag set, storage cleared');
      
      // CRITICAL: Wait for Supabase to fully sign out (clears localStorage session)
      const { error } = await supabase.auth.signOut();
      if (error) {
        appLogger.error('SIGN OUT: Supabase error', error);
        // Continue anyway - local state is cleared
      }
      
      appLogger.debug('SIGN OUT: Complete, redirecting to login');
      
      // Force redirect to login page with full reload
      // This ensures no cached state is used
      window.location.replace('/login');
      
    } catch (error) {
      appLogger.error('Error signing out', error);
      // Even on error, set flag and redirect
      localStorage.setItem('user_signed_out', Date.now().toString());
      sessionStorage.clear();
      window.location.replace('/login');
    }
  };

  return {
    ...authState,
    signOut,
    isLoading: authState.loading
  };
};
